import UserModel from './models/UserModel';
import axiosHelper from '@/helpers/axiosHelper';
import UserSettingsModel from './models/UserSettingsModel';

const userModule = {
	state() {
		return {
			user: null,
			userSettings: null,
			jwt: null,
			refreshToken: null
		}
	},
	actions: {
		async authenticateAsync(ctx: any, payload: any) {
			const login = payload.login;
			const password = payload.passw;

			let jwt;

			try {
				const authHttpClient = axiosHelper.createInstance();
				const response = await authHttpClient.post('/api/auth', { login, password });
				jwt = response.data as string;
			} catch (e: any) {
				if (e.status === 401) {
					console.warn('Unable to authenticate: wrong credentials');
					return false;
				}

				console.error('Unable to authenticate', e);
				return false;
			}

			try {
				const userHttpClient = axiosHelper.createInstance(jwt);
				const userResponse = await userHttpClient.get('/api/auth/user');
				const user = userResponse.data;

				if (user) {
					ctx.commit('jwt', jwt);
					ctx.commit('user', new UserModel(user));
					return true;
				} else {
					throw new Error('unable to fetch user with jwt');
				}
			} catch (e) {
				return null;
			}
		},

		async getRefreshTokenWithJwtAsync(ctx: any) {

			try {
				const jwt = ctx.getters.jwt;
				if (!jwt?.length) {
					throw new Error('valid JWT is missing');
				}

				const refreshTokenHttpClient = axiosHelper.createInstance(jwt);
				const rt = await refreshTokenHttpClient.get('/api/auth/refreshToken');
				const refreshToken = rt.data;
				ctx.commit('refreshToken', refreshToken);

			} catch (e) {
				console.error('Unable to get refresh token', e);
				throw e;
			}
		},

		/** get actual JWT with provided refresh-token  */
		async exchangeRefreshTokenForJwt(ctx: any) {
			try {
				const refreshToken = ctx.getters.refreshToken;
				if (!refreshToken?.length) {
					return false;
				}

				const jwtHttpClient = axiosHelper.createInstance();
				const jwtResponse = await jwtHttpClient.post(`/api/auth/jwt`, { refreshToken });
				const jwt = jwtResponse.data;
				ctx.commit('jwt', jwt);
				return true;
			} catch (e: any) {
				if (e?.status == 401) {
					ctx.commit('signOut');
					console.warn('Unable to exchange JWT token: invalid/expired refresh-token or user is blocked');
					return false;
				}

				console.error('Unable to exchange JWT token for refreshToken', e);
				throw e;
			}
		},

		async fetchUserAsync(ctx: any) {
			try {
				const jwt = ctx.getters.jwt;
				if (!jwt?.length) {
					return false;
				}

				const userHttpClient = axiosHelper.createInstance(jwt);
				const userResponse = await userHttpClient.get(`/api/auth/user`);
				const user = new UserModel(userResponse.data);

				if (!user?.id) {
					throw new Error('invalid/empty response received');
				}

				ctx.commit('user', user);
				return true;
			} catch (e) {
				console.error('Unable to fetch user', e);
				throw e;
			}
		},

		async renewJwtAsync(ctx: any) {
			try {
				const jwt = ctx.getters.jwt;
				if (!jwt?.length) {
					throw new Error('valid JWT is missing');
				}

				const jwtHttpClient = axiosHelper.createInstance(jwt);
				const userResponse = await jwtHttpClient.get(`/api/auth/jwt`);
				const user = new UserModel(userResponse.data);

				if (!user?.id) {
					throw new Error('invalid/bad response received');
				}

				ctx.commit('jwt', user);
				return true;
			} catch (e) {
				console.error('Unable to renew JWT', e);
				return false;
			}
		}
	},
	mutations: {
		jwt(state: any, payload: string) {
			state.jwt = payload;
		},
		refreshToken(state: any, payload: string) {
			state.refreshToken = payload;
		},
		user(state: any, payload: UserModel) {
			state.user = payload;
		},
		userSettings(state: any, payload: UserSettingsModel) {
			state.userSettings = payload;
		},
		signOut(state: any) {
			state.refreshToken = null;
			state.user = null;
			state.jwt = null;
		}
	},
	getters: {
		user(state: any): UserModel {
			return new UserModel(state.user);
		},
		userSettings(state: any): UserSettingsModel {
			return new UserSettingsModel(state.userSettings);
		},
		jwt(state: any): string {
			return state.jwt;
		},
		refreshToken(state: any): string {
			return state.refreshToken;
		},
		isAuthenticated(state: any): boolean {
			return (
				!!state.user &&
				!!state.user?.id &&
				!state.user?.blocked &&
				(state.refreshToken?.length > 1));
		},
	}
};

export default userModule;